<template>
  <listing-layout>
    <h1 slot="heading" class="sr-only">Join Members</h1>
    <div slot="content">
      <h2 slot="heading" class="mb-0">Select Members To Join</h2>
      <h3 class="h4 mb-4">Joining members to {{ primary.first_name + ' ' + primary.last_name }}</h3>
      <div class="alert alert-success" v-if="successMessage">{{ successMessage }}</div>
      <div class="alert alert-warning" v-else-if="errorMessage">{{ errorMessage }}</div>

      <div class="form-inline mb-sm-3 d-flex justify-content-between">
        <div class="d-flex col-12 col-md-auto px-0 mb-3 mb-md-2">
          <label for="showFinancial-all" class="sr-only">Financial</label>
          <div class="filter">
            <div class="form-check pl-0" v-for="tab in FINANCIAL_TABS" :key="tab.value">
              <input
                class="form-check-input"
                type="radio"
                name="showFinancial"
                :id="'showFinancial-' + tab.value"
                :value="tab.value"
                :checked="filters.financial === tab.value"
                @change="setFilter('financial', tab.value)"
              >
              <label class="form-check-label" :for="'showFinancial-' + tab.value">{{ tab.label }}</label>
            </div>
          </div>
        </div>

        <div class="form-group col-12 col-lg-auto mt-2 mt-lg-0 mt-xl-0 ml-auto px-0 action_section">
          <label for="joinMembersSearch" class="d-none d-lg-inline mr-xl-2 mb-0 pr-2 font-weight-normal">Search</label>
          <div class="input-append d-flex flex-grow-1 flex-md-grow-0 mr-md-2">
            <input
              id="joinMembersSearch"
              v-model="searchInput"
              type="text"
              class="form-control mb-0 flex-grow-1 flex-md-grow-0"
              @keyup.enter="applySearch"
              @search="applySearch"
            >
            <svg
              class="append"
              role="button"
              aria-label="Search"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 14 14"
              width="16"
              height="16"
              @click="applySearch"
            >
              <rect width="100%" height="100%" fill="none" />
              <g>
                <path fill="#231815" d="M5.5 11C2.5 11 0 8.5 0 5.5S2.5 0 5.5 0 11 2.5 11 5.5 8.5 11 5.5 11zm0-10C3 1 1 3 1 5.5S3 10 5.5 10 10 8 10 5.5 8 1 5.5 1z" />
                <path fill="#231815" d="M9.5 8.8l4.4 4.4c.2.2.2.5 0 .7l-.4.1-.4-.1-4.4-4.4.8-.7z" />
              </g>
            </svg>
          </div>
        </div>
      </div>

      <div class="position-relative" style="min-height: 240px">
        <responsive-table
          :columns="columns"
          :rows="rows"
          :loading="loading"
          :breakpoints="breakpoints"
          :sort="filters.sort"
          :direction="filters.direction"
          empty-text="No members found."
          @sort="setSort"
        >
          <template #cell:select="{ row }">
            <div class="custom-control custom-checkbox custom-control-inline mr-0">
              <input
                type="checkbox"
                :id="`joinSelect${row.id}`"
                class="custom-control-input"
                :checked="isSelected(row.id)"
                @change="toggleSelect(row)"
              >
              <label class="custom-control-label" :for="`joinSelect${row.id}`"></label>
            </div>
          </template>

          <template #cell:name="{ row }">
            <list-name-cell
              :label="`${row.first_name} ${row.last_name}`"
              :to="{ name: 'Member Profile', params: { slug: row.id } }"
            >
              <template #badges>
                <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              </template>
            </list-name-cell>
          </template>

          <template #cell:first_name="{ row }">
            <router-link :to="{ name: 'Member Profile', params: { slug: row.id } }">{{ row.first_name }}</router-link>
          </template>
          <template #cell:last_name="{ row }">
            <list-name-cell
              align="right"
              :label="row.last_name"
              :to="{ name: 'Member Profile', params: { slug: row.id } }"
            >
              <template #badges>
                <span class="badge" :class="statusBadgeClass(row)">{{ statusBadgeLabel(row) }}</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:member_number="{ row }">
            <list-name-cell align="right" :label="row.member_number">
              <template #badges>
                <span class="badge alert-prim">Primary</span>
              </template>
            </list-name-cell>
          </template>
          <template #cell:zone_id="{ row }">{{ zoneCode(row.zone_id) }}</template>
        </responsive-table>
      </div>

      <div class="form-inline justify-content-between align-items-start mt-3">
        <div class="d-flex align-items-center justify-content-center justify-content-md-start form-group mb-3 mb-md-0 col-12 col-md-auto px-0">
          <label for="joinMembersShow" class="mr-2 mb-0 font-weight-normal">Show</label>
          <select
            id="joinMembersShow"
            class="form-control mr-2"
            :value="filters.per_page"
            @change="setPerPage(Number($event.target.value))"
          >
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
          <span>Entries</span>
        </div>

        <div class="pagination-container form-group align-items-start justify-content-center mb-3 mb-md-0" v-show="totalPages > 1">
          <pagination
            :total-pages="totalPages"
            :page="page"
            :on-change="setPage"
            :disabled="loading"
          />
        </div>
      </div>

      <div class="sticky-bottom alert bg-primary text-white d-flex flex-wrap align-items-center mb-0" role="alert" v-if="selected.length > 0">
        <div class="flex-grow-1 pr-3 text-center text-lg-left">
          <strong>You have selected: </strong>
          <span v-for="(row, i) in selected" :key="row.id">
            {{ row.first_name }} {{ row.last_name }}{{ i === selected.length - 1 ? '' : ',' }}
          </span>
        </div>
        <button class="btn btn-outline-white btn-block-mob mt-2 mt-lg-0 text-nowrap" @click="joinMembers">Join Selected</button>
        <button class="btn btn-outline-white ml-lg-2 btn-block-mob" @click.prevent="selected = []">Cancel</button>
      </div>
    </div>
  </listing-layout>
</template>

<script>
import axios from 'axios'
import { defineComponent, ref, reactive, watch, onMounted, onBeforeMount, onBeforeUnmount, getCurrentInstance } from '@vue/composition-api'
import listingLayout from '@/components/ListingLayout'
import ResponsiveTable from '@/components/ResponsiveTable'
import Pagination from '@/components/Pagination'
import ListNameCell from '@/components/ListNameCell'
import membersApi from '@/services/api/members'
import { buildColumns, buildBreakpoints, FINANCIAL_TABS } from './columns'

const PER_PAGE_OPTIONS = [20, 50, 100, 200]

export default defineComponent({
  name: 'MemberJoin',
  components: {
    'listing-layout': listingLayout,
    ResponsiveTable,
    Pagination,
    ListNameCell
  },
  props: {
    primary: { type: Object, default: undefined }
  },
  setup(props) {
    const { proxy } = getCurrentInstance()
    const store = proxy.$store

    onBeforeMount(() => {
      if (props.primary === undefined) {
        if (proxy.$isAdmin) proxy.$router.push('/admin-members')
        proxy.$router.push('/members')
      }
    })

    // Deliberately NOT useServerList here (unlike Members.vue/Horses.vue): that composable syncs
    // state via `router.replace({ query })` on every search/page/sort change. This route has no
    // dynamic path segment, so vue-router's matcher (normalizeLocation/match, vue-router 3.1.5)
    // resolves a query-only replace by path and rebuilds `route.params` from scratch — wiping the
    // in-memory `primary` object this whole page depends on (it never lived in the URL, only in
    // route.params from the router.push() that opened this page). Kept as page-local state with
    // no router calls instead — this screen was never reload/bookmark-safe anyway (a hard refresh
    // already loses `primary` today, hence the existing beforeMount redirect-if-undefined below).
    const rows = ref([])
    const loading = ref(false)
    const page = ref(1)
    const totalPages = ref(1)
    const perPageOptions = PER_PAGE_OPTIONS
    const filters = reactive({ sort: 'last_name', direction: 'asc', per_page: 20, search: '', financial: 'all' })

    function buildParams() {
      const params = { page: page.value, per_page: filters.per_page, primary_only: 1 }
      if (filters.sort) {
        params.sort = filters.sort
        params.direction = filters.direction
      }
      if (filters.search) params.search = filters.search
      if (filters.financial !== 'all') params.financial = filters.financial
      return params
    }

    async function fetch() {
      loading.value = true
      try {
        const res = await membersApi.getList(buildParams())
        const body = res.data != null ? res.data : {}
        rows.value = body.data != null ? body.data : []
        const meta = body.meta != null ? body.meta : {}
        page.value = meta.current_page != null ? meta.current_page : 1
        totalPages.value = meta.last_page != null ? meta.last_page : 1
      } catch (_) {
        rows.value = []
      } finally {
        loading.value = false
      }
    }

    function setPage(p) {
      page.value = p
      fetch()
    }
    function setSort(field) {
      if (filters.sort !== field) {
        filters.sort = field
        filters.direction = 'asc'
      } else if (filters.direction === 'asc') {
        filters.direction = 'desc'
      } else {
        filters.sort = 'last_name'
        filters.direction = 'asc'
      }
      page.value = 1
      fetch()
    }
    function setPerPage(n) {
      filters.per_page = Number(n)
      page.value = 1
      fetch()
    }
    function setSearch(value) {
      filters.search = value || ''
      page.value = 1
      fetch()
    }
    function setFilter(key, value) {
      filters[key] = value
      page.value = 1
      fetch()
    }

    const columns = buildColumns()
    const breakpoints = buildBreakpoints()

    function zoneCode(id) {
      const zone = (store.state.listZones || []).find(z => z.id === id)
      return zone ? zone.code : 'Unspecified'
    }

    // Mirrors Members.vue's status badge (same Text.vue `showActive` rule the legacy page used
    // on this column too — see the `showActive: true` option on the old Last Name template cell).
    function statusBadgeLabel(row) {
      if (row.membership_status !== 'Active') return row.membership_status === 'Onhold' ? 'On Hold' : row.membership_status
      return row.financial ? 'Financial' : 'Unfinancial'
    }
    function statusBadgeClass(row) {
      if (row.membership_status !== 'Active') {
        if (row.membership_status === 'Lifetime') return 'alert-info'
        if (row.membership_status === 'Archived') return 'alert-dark'
        return 'alert-danger'
      }
      return row.financial ? 'alert-info' : 'alert-warning'
    }

    const searchInput = ref(filters.search || '')
    let searchTimer = null
    function applySearch() {
      clearTimeout(searchTimer)
      if (searchInput.value !== filters.search) setSearch(searchInput.value)
    }
    onBeforeUnmount(() => clearTimeout(searchTimer))
    watch(searchInput, (val) => {
      clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        if (val !== filters.search) setSearch(val)
      }, 500)
    })

    // Selection lives here, keyed by id, entirely separate from `rows` (the currently loaded
    // page). Unlike the shared Vuex `selectedRows`/`updateSelectedRows` pattern used by
    // DataTable-based pages elsewhere (HorseRegister, NominateRookie), it survives pagination,
    // sorting and search without needing the underlying row objects to keep the same identity —
    // see the CAM-1477 part 2 task file for why that global pattern doesn't survive a refetch.
    const selected = ref([])
    function isSelected(id) {
      return selected.value.some(row => row.id === id)
    }
    function toggleSelect(row) {
      const index = selected.value.findIndex(r => r.id === row.id)
      if (index !== -1) selected.value.splice(index, 1)
      else selected.value.push(row)
    }

    const successMessage = ref('')
    const errorMessage = ref('')
    function joinMembers() {
      axios.post(`${proxy.$api}/admin/join`, {
        primary_id: props.primary.id,
        secondary_ids: selected.value.map(row => row.id)
      }).then((response) => {
        if (response.data.status === 'success') {
          proxy.$router.push({ name: 'Member Profile', params: { details: props.primary, tab: 'membership', success: 'The members have been joined successfully.' } })
          errorMessage.value = ''
        } else if (response.data.status === 'existing_child') {
          successMessage.value = ''
          errorMessage.value = `${response.data.data.map(obj => obj.name).join(', ')} are already part of another membership.`
        } else if (response.data.status === 'is_parent') {
          successMessage.value = ''
          errorMessage.value = `${response.data.data.map(obj => obj.name).join(', ')} are already in another membership. Only members not in existing memberships can be joined.`
        } else if (response.data.status === 'self_join') {
          successMessage.value = ''
          errorMessage.value = "You've attempted to join a user to itself. Please select different users."
        }
      }).catch((error) => {
        // eslint-disable-next-line no-console
        console.log(error)
      })
    }

    onMounted(fetch)

    return {
      columns,
      breakpoints,
      rows,
      loading,
      page,
      totalPages,
      filters,
      perPageOptions,
      searchInput,
      applySearch,
      setFilter,
      setPage,
      setSort,
      setPerPage,
      FINANCIAL_TABS,
      zoneCode,
      statusBadgeLabel,
      statusBadgeClass,
      selected,
      isSelected,
      toggleSelect,
      successMessage,
      errorMessage,
      joinMembers
    }
  }
})
</script>



// WEBPACK FOOTER //
// src/views/Members/Join/MemberJoin.vue